import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSite } from '../store/useSite';
import { usePwaStore } from '../store/usePwaStore';
import environment from '../environments/environment';

/**
 * PWA Install Banner Component
 * Displays custom 1:1 Event Icon, 2-line clean mobile text formatting, dynamic event title, and original blue theme.
 */
export const PwaInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  const location = useLocation();
  const isDashboard = location.pathname.includes('/dashboard');

  // Extract current event details from store or URL slug
  const eventData = useSite((state) => state.eventData);
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentSlug = eventData?.slug || (pathParts.length > 0 && !isDashboard ? pathParts[0] : '');
  const eventName = eventData?.event_name || eventData?.name || '';

  const {
    deferredPrompt,
    isIos,
    isStandalone,
    isInstalled,
    initPwaListeners,
    promptInstall,
  } = usePwaStore();

  useEffect(() => {
    const cleanup = initPwaListeners();
    return cleanup;
  }, [initPwaListeners]);

  // Icon URL priority: pwa_icon -> backend icon route -> default logo512
  const apiBase = environment.apiUrl || '';
  const iconSrc = eventData?.pwa_icon
    ? (eventData.pwa_icon.startsWith('http') ? eventData.pwa_icon : `${apiBase}/${eventData.pwa_icon.replace(/^\//, '')}`)
    : (currentSlug ? `${apiBase}/manifest/icon/${currentSlug}` : '/logo512.png');

  useEffect(() => {
    if (isStandalone || isInstalled || isDashboard) {
      setShowBanner(false);
      return;
    }

    if (isIos || deferredPrompt) {
      setShowBanner(true);
    }
  }, [isStandalone, isInstalled, isDashboard, isIos, deferredPrompt]);

  const handleInstallClick = async () => {
    if (isIos) {
      setShowIosGuide((prev) => !prev);
      return;
    }

    const res = await promptInstall();
    if (res?.outcome === 'accepted') {
      setShowBanner(false);
    }
  };

  const handleClose = () => {
    setShowBanner(false);
  };

  if (!showBanner || isDashboard || isStandalone || isInstalled) {
    return null;
  }

  return (
    <div className="fixed bottom-3 left-3 right-3 md:left-auto md:right-6 md:bottom-6 z-[9999] md:max-w-md bg-[#1A56DB] text-white p-3 sm:p-3.5 rounded-xl shadow-2xl flex flex-col gap-2 font-sans border border-blue-400/40 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex items-center justify-between gap-2.5">
        {/* Event Icon & 2-Line Text */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg overflow-hidden shrink-0 bg-white/10 border border-white/20 shadow-sm flex items-center justify-center">
            <img
              src={iconSrc}
              alt="App Icon"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/logo512.png';
              }}
            />
          </div>
          <div className="flex flex-col text-xs sm:text-sm font-medium leading-tight min-w-0">
            <span className="font-bold text-white tracking-wide truncate">
              {eventName ? `Install ${eventName} App` : 'Install this Event App'}
            </span>
            <span className="text-blue-100 font-normal">
              to your Home Screen!
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={handleInstallClick}
            className="bg-white text-[#1A56DB] hover:bg-blue-50 font-bold px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm shadow transition-all active:scale-95 whitespace-nowrap"
          >
            {isIos ? (showIosGuide ? 'Hide' : 'Install') : 'Install Now'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 hover:bg-blue-700/60 rounded-lg text-blue-100 transition-colors"
            title="Dismiss"
            aria-label="Close"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* iPhone Safari Guide */}
      {isIos && showIosGuide && (
        <div className="pt-2.5 mt-1 border-t border-blue-400/40 text-xs text-blue-50 flex items-start justify-between gap-2">
          <div className="space-y-1">
            <p className="font-semibold text-white">To install on iPhone / iPad (Safari):</p>
            <p>1. Tap <strong>Share</strong> ( ⎋ ) in Safari menu</p>
            <p>2. Select <strong>Add to Home Screen</strong> ( ➕ )</p>
          </div>
          <button
            type="button"
            onClick={() => setShowIosGuide(false)}
            className="text-xs bg-white/20 hover:bg-white/30 text-white font-medium px-2 py-1 rounded-md transition-colors shrink-0"
          >
            Close Guide
          </button>
        </div>
      )}
    </div>
  );
};

export default PwaInstallBanner;
